/**
 * Data Formatters for SAA Trading Dashboard
 * Purpose: Ensure consistent, professional data display
 * No more 0.6921936493420779% nonsense!
 */

export const formatters = {
  /**
   * Format percentage with max 2 decimals
   * 0.006921936493420779 → 0.69%
   */
  percentage: (value: number, decimals: number = 2): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return `${value.toFixed(decimals)}%`;
  },

  /**
   * Format volatility with 1 decimal
   * 0.006921936493420779 → 0.7%
   */
  volatility: (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  },

  /**
   * Format price intelligently
   * BTC: $93,037 (no decimals)
   * ETH: $3,186 (no decimals)
   * Altcoins < $100: $12.45 (2 decimals)
   */
  price: (value: number, asset?: string): string => {
    if (value === null || value === undefined || isNaN(value) || value === 0) {
      return '<span class="text-yellow-400">Updating...</span>';
    }

    const isMajor = asset && ['btc', 'eth', 'bnb'].includes(asset.toLowerCase());
    
    if (isMajor || value >= 100) {
      return `$${value.toLocaleString('en-US', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      })}`;
    }
    
    return `$${value.toFixed(2)}`;
  },

  /**
   * Format currency with smart units
   * 45200000000 → $45.2B
   * 1850000000 → $1.85B
   */
  currency: (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return '<span class="text-red-400">N/A</span>';
    }

    // CRITICAL: Handle zero volume bug!
    if (value === 0) {
      return '<span class="text-red-400">Data unavailable</span>';
    }

    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
    return `$${value.toFixed(2)}`;
  },

  /**
   * Format score (no decimals!)
   * 92.3456 → 92
   */
  score: (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return Math.round(value).toString();
  },

  /**
   * Format volume already in billions: 1–2 decimals, B or T
   * 846.69 → $846.7B, 1200 → $1.2T
   */
  billionVolume: (valueInBillions: number): string => {
    if (valueInBillions === null || valueInBillions === undefined || isNaN(valueInBillions)) return 'N/A';
    if (valueInBillions >= 1000) return `$${(valueInBillions / 1000).toFixed(1)}T`;
    if (valueInBillions >= 1) return `$${valueInBillions.toFixed(1)}B`;
    return `$${valueInBillions.toFixed(2)}B`;
  },

  /**
   * Format large numbers with commas
   * 850000 → 850,000
   */
  number: (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return value.toLocaleString('en-US');
  },

  /**
   * Format exchange netflow (value in millions USD)
   * -3561.02 → -$3.56B
   * 850 → +$850M
   */
  netflow: (valueInMillions: number): string => {
    if (valueInMillions === null || valueInMillions === undefined || isNaN(valueInMillions)) return 'N/A';
    const sign = valueInMillions >= 0 ? '+' : '';
    const abs = Math.abs(valueInMillions);
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(2)}B`;
    return `${sign}$${abs.toFixed(abs >= 10 ? 1 : 2)}M`;
  },

  /**
   * Format change percentage with +/- sign
   * 3.24 → +3.24%
   * -2.15 → -2.15%
   */
  change: (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  },

  /**
   * Safe display - handles null/undefined/zero
   */
  safe: (value: any, formatter?: (val: any) => string, fallback: string = 'N/A'): string => {
    if (value === null || value === undefined || value === '' || (typeof value === 'number' && isNaN(value))) {
      return `<span class="text-gray-400">${fallback}</span>`;
    }
    return formatter ? formatter(value) : String(value);
  }
};

/**
 * Ticker price — single documented significant-figures rule so precision is
 * consistent across every asset in the strip (no ad-hoc 6-vs-2 decimals).
 * Rule:
 *   • price ≥ 1      → 2 decimals with thousands separators ($50.30, $1,234.56)
 *   • 0 < price < 1  → 4 significant figures ($0.1599, $0.9993)
 *   • price ≤ 0/NaN  → em dash
 * Sub-$1 uses significant figures (not fixed decimals) so a $0.000123 token and
 * a $0.99 token both carry the same information density.
 */
export const tickerPriceUSD = (value: number): string => {
  if (value === null || value === undefined || isNaN(value) || value <= 0) return '—';
  if (value >= 1) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  // 4 significant figures for sub-dollar prices.
  return `$${value.toPrecision(4)}`;
};

/**
 * ISO 8601 UTC timestamp for institutional display: "2026-06-10 11:55 UTC".
 * Unambiguous (no m/d/y locale guessing), minute precision, explicit zone.
 */
export const utcIso8601 = (input: Date | string | number): string => {
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return 'N/A';
  // toISOString → "2026-06-10T11:55:23.000Z"; take date + HH:mm, label UTC.
  const iso = d.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
};

// Export individual formatters for convenience
export const {
  percentage,
  volatility,
  price,
  currency,
  score,
  billionVolume,
  number,
  netflow,
  change,
  safe
} = formatters;

export default formatters;

